import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import logo from "../../asset/CODEHOUZE-1.jpg";

const navLinks = [
  { href: "#services", label: "Services", id: "services" },
  { href: "#work", label: "Work", id: "work" },
  { href: "#about", label: "About", id: "about" },
  { href: "#contact", label: "Contact", id: "contact" },
];

const CodehouzeHeader = ({ onStartProject }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const sectionIds = ["home", "services", "work", "about", "contact"];
    const observers = sectionIds.map((id) => {
      const el = document.getElementById(id);
      if (!el) return null;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { rootMargin: "-40% 0px -55% 0px" }
      );
      observer.observe(el);
      return observer;
    });

    return () => observers.forEach((obs) => obs?.disconnect());
  }, []);

  const linkClass = (id) =>
    `rounded-full px-3.5 py-1.5 text-sm transition-all duration-200 ${
      activeSection === id
        ? "text-white bg-codehouzeOrange/90"
        : "text-gray-400 hover:text-white hover:bg-white/5"
    }`;

  return (
    <div className="sticky top-0 z-40 px-3 pt-3 sm:px-6 sm:pt-4">
      <nav
        className={`mx-auto max-w-5xl rounded-2xl transition-all duration-300 ${
          scrolled
            ? "bg-gray-900/70 backdrop-blur-xl border border-white/10 shadow-lg shadow-black/30"
            : "bg-gray-900/40 backdrop-blur-md border border-white/5"
        }`}
      >
        <div className="px-4 sm:px-5">
          <div className="flex h-14 items-center justify-between">
            <a href="#home" className="flex items-center gap-2.5">
              <span className="bg-white rounded-lg p-1 flex items-center justify-center">
                <img src={logo} alt="Codehouze" className="h-6 w-6 object-contain" />
              </span>
              <span className="text-white font-semibold text-sm tracking-wide">
                Codehouze
              </span>
            </a>

            <div className="hidden sm:flex items-center gap-1">
              {navLinks.map(({ href, label, id }) => (
                <a key={href} href={href} className={linkClass(id)}>
                  {label}
                </a>
              ))}
              <button
                type="button"
                onClick={onStartProject}
                className="ml-2 text-white bg-gradient-to-r from-codehouzeMaroon to-codehouzeOrange hover:brightness-110 rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200 shadow-md shadow-codehouzeOrange/20"
              >
                Start a Project
              </button>
            </div>

            <button
              type="button"
              className="sm:hidden p-2 rounded-full text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
              aria-expanded={isOpen}
              onClick={() => setIsOpen(!isOpen)}
            >
              <span className="sr-only">Toggle menu</span>
              {isOpen ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="sm:hidden border-t border-white/10 overflow-hidden"
            >
              <div className="px-4 py-3 space-y-1">
                {navLinks.map(({ href, label, id }) => (
                  <a
                    key={href}
                    href={href}
                    className={`block ${linkClass(id)}`}
                    onClick={() => setIsOpen(false)}
                  >
                    {label}
                  </a>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    onStartProject();
                  }}
                  className="block w-full text-white bg-gradient-to-r from-codehouzeMaroon to-codehouzeOrange rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200 text-center"
                >
                  Start a Project
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </div>
  );
};

export default CodehouzeHeader;
